import React from "react";
import slugify from "slugify";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { sendAnalyticsEvent } from "../utils/analytics";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const EVENT_TOPICS = [
  {
    event_topic_id: -1,
    name: "All Yocket Events",
    slug: "all-events",
    colorCode: "#F2D210",
    inactive_icon_url:
      "https://d15gkqt2d16c1n.cloudfront.net/images/calendarIcon.svg",
    active_icon_url:
      "https://d15gkqt2d16c1n.cloudfront.net/images/calendar_active.svg",
  },
  {
    event_topic_id: 1,
    name: "Basics of Study Abroad",
    colorCode: "#16A34A",
  },
  {
    event_topic_id: 2,
    name: "University Meet-ups",
    colorCode: "#CD3D36",
  },
  {
    event_topic_id: 3,
    name: "Alumni Networking",
    colorCode: "#00ACBB",
  },
  {
    event_topic_id: 4,
    name: "Test Prep",
    colorCode: "#7C3AED",
  },
  {
    event_topic_id: 5,
    name: "Application Processing",
    colorCode: "#CA8A04",
  },
  {
    event_topic_id: 6,
    name: "Loans & Visa Process",
    colorCode: "#0275D8",
  },
];

const EventCard = ({ event }) => {
  const imageUrl = `${process.env.REACT_APP_CLOUDFRONT_CDN_DOMAIN}/images/events/`;
  const defaultBgUrl = `${process.env.REACT_APP_CLOUDFRONT_CDN_DOMAIN}/images/dashboard/profile/default-bg-image-profile.jpg`;

  const goToEventDescription = () => {
    const eventSlug = slugify(event.name) + "-" + event.event_id;
    let queryUrl = "";
    if (origin === "homePage") {
      queryUrl = `?frq=${event.frequency}`;
      if (event.event_venue_id) {
        queryUrl = queryUrl + `&venue=${event.event_venue_id}`;
      }
    }

    if (event.event_type === "attended") {
      queryUrl = queryUrl + "?attended=1";
    }

    // Webengage
    sendAnalyticsEvent({
      WE: {
        name: "event_details_click",
        data: {
          event_id: event.event_id,
          event_name: event.name,
          event_type: event.event_type,
          source_module: "event_end_page",
        },
      },
    });

    window.top.location.href =
      process.env.REACT_APP_VUE_HYDROGEN_REDIRECT +
      `/events/${eventSlug}${queryUrl}`;
  };

  const getEventVenueType = () => {
    let eventVenueType = "";
    if (event.event_venue_type === "VIRTUAL") {
      eventVenueType = "Virtual Event";
    } else if (event.event_venue_type === "PHYSICAL") {
      eventVenueType = `Physical Meetup`;
      if (event.city_name) {
        eventVenueType += `<span className="text-yocket-blue-600"> @${event.city_name}</span>`;
      }
    }
    return eventVenueType;
  };

  const getEventTime = () => {
    if (event.date_range_for_multislot) {
      return event.date_range_for_multislot;
    }
    return `${dayjs(event.starting_time).format(
      "ddd, D MMM • h:mm A"
    )} - ${dayjs(event.starting_time)
      .add(event.duration, "hours")
      .format("h:mm A")} ${"IST"} `;
  };

  const getVenueTypeIcon = () => {
    let eventIcon = "";
    if (event.event_venue_type === "VIRTUAL") {
      eventIcon = "https://assets.yocket.com/svgs/videoIconLight.svg";
    } else if (event.event_venue_type === "PHYSICAL") {
      eventIcon = "/svgs/location-pin.svg";
    }
    return eventIcon;
  };

  const getEventTopic = () => {
    const event_topic = event.event_topic[0];
    event_topic.colorCode = EVENT_TOPICS.find(
      topic => topic.event_topic_id === event_topic.event_topic_id
    )?.colorCode;
    return event_topic;
  };

  const getSecondaryTopic = () => {
    const topic = event.event_topic.find(
      topic => topic.name !== getEventTopic().name
    );
    return topic?.name || "none";
  };

  return (
    <div
      className="flex flex-col bg-white border border-yocket-neutral-200 rounded-xl overflow-hidden min-w-[300px] md:max-w-[358px] shadow-md hover:shadow-lg md:cursor-pointer w-full"
      onClick={goToEventDescription}
    >
      {/* Image */}
      <div className="relative flex flex-col">
        <img
          src={
            event.cover_img_url ? imageUrl + event.cover_img_url : defaultBgUrl
          }
          alt={event.name}
          className="h-auto max-h-[150px] w-full object-cover aspect-[16/9]"
        />

        {/* Live Tag */}
        {event.event_type && event.event_type.toLowerCase() === "live" && (
          <div className="w-16 h-9 py-2 px-3 rounded-[25.75rem] bg-yocket-red-400 flex items-center space-x-1 absolute top-2 left-2">
            <div className="w-2.5 h-2.5 border-2 border-yocket-neutral-0 rounded-full animate__animated animate__flash animate__infinite animate__slow" />
            <p className="font-bold text-yocket-neutral-0 text-sm">Live</p>
          </div>
        )}

        {/* Must Attend Tag */}
        {event.ordering === 1 && (
          <img
            src="https://assets.yocket.com/svgs/ribbonStar.svg"
            alt=""
            className={
              "absolute -top-1" +
              (event.free_user_visible
                ? "right-[0.761rem]"
                : "right-[3.306rem]")
            }
            title="Must Attend"
          />
        )}

        {/* Premium Event Tag */}
        {!event.free_user_visible && (
          <img
            src="https://assets.yocket.com/svgs/ribbonCrown.svg"
            alt=""
            className="absolute -top-1 right-[0.761rem]"
            title="Premium Only"
          />
        )}
      </div>

      {/* Event Details */}
      <div className="flex flex-col space-y-6 p-4 w-full justify-between grow">
        <p
          className="font-bold text-base line-clamp-2 cursor-pointer hover:underline"
          onClick={e => {
            e.stopPropagation();
            goToEventDescription();
          }}
        >
          {event.name}
        </p>

        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex gap-2 items-center">
              <img
                src="/svgs/calendar.svg"
                className="h-4 w-4"
                alt="event-date-time"
              />
              <p className="text-sm font-medium text-yocket-neutral-500">
                {getEventTime()}
              </p>
            </div>

            <div
              className="flex items-center gap-2"
              onClick={() => goToEventDescription()}
            >
              <img
                src="/svgs/location-pin-grey.svg"
                className="h-4 w-4"
                alt="mode-of-event"
              />
              <p
                className="text-sm font-medium text-yocket-neutral-500"
                dangerouslySetInnerHTML={{ __html: getEventVenueType() }}
              />
            </div>
          </div>

          {/* CTAs */}
          <div className="flex items-center justify-between">
            {event.free_user_visible && (
              <div className="py-1 px-3 text-sm border rounded-md border-[#BBF7D0] bg-[#F0FDF4] text-[#008744] font-bold">
                FREE
              </div>
            )}
            <button
              className="pl-5 py-2.5 text-yocket-orange-700 font-bold text-sm w-fit flex items-center gap-1.5"
              onClick={e => {
                e.stopPropagation();
                goToEventDescription();
              }}
            >
              <span>Know More</span>
              <img
                src="/svgs/chevron-orange.svg"
                alt="chevron"
                className="h-5 w-5"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCard;
